
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Phone, Plus, Search, Pencil, Eye,
  Building2, User, TrendingUp, Calendar,
  ChevronLeft, ChevronRight, Upload, UserPlus, X, ArrowRight,
} from "lucide-react";
import { fetchCallLogs, deleteCallLog, convertCallToLead } from "../callLogApi";
import api from "../api/api";
import { formatCallStatus } from "../utils/callStatusLabels";
import CallLogModal    from "../components/callLogs/CallLogModal";
import BulkUploadModal from "../components/callLogs/BulkUploadModal";
import "./CallLogPage.css";
import "../css/callLogs/callToLead.css";
import "../css/callLogs/callLogsModern.css";

const STATUS_COLOR = {
  "Not Answering":  { bg: "#f3f4f6", color: "#6b7280" },
  Interested:       { bg: "#f0fdf4", color: "#16a34a" },
  "Not Interested": { bg: "#fef2f2", color: "#dc2626" },
  "Follow-up":      { bg: "#faf5ff", color: "#7c3aed" },
};

const VISIT_COLOR = {
  Pending:   "#6b7280",
  Scheduled: "#2563eb",
  Completed: "#16a34a",
  Cancelled: "#dc2626",
};

const PAGE_SIZE = 10;

function LinkedEntities({ log }) {
  const items = [];
  if (log.lead)
    items.push(
      <span key="lead" className="clp-tag lead" title={`Status: ${log.lead.status || ""}`}>
        <User size={11}/> {log.lead.full_name || log.lead.name || "Lead"}
      </span>
    );
  if (log.property)
    items.push(
      <span key="prop" className="clp-tag prop" title={log.property.location || ""}>
        <Building2 size={11}/> {log.property.propertyName || "Property"}
      </span>
    );
  if (log.deal)
    items.push(
      <span key="deal" className="clp-tag deal" title={`Stage: ${log.deal.stage || ""}`}>
        <TrendingUp size={11}/> {log.deal.dealName || "Deal"}
      </span>
    );
  return items.length > 0
    ? <div className="clp-linked">{items}</div>
    : <span className="clp-none">—</span>;
}

export default function CallLogPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [from,    setFrom]    = useState("");
  const [to,      setTo]      = useState("");
  const [page,    setPage]    = useState(1);
  const [modal,   setModal]   = useState(false);
  const [bulk,    setBulk]    = useState(false);
  const [editing, setEditing] = useState(null);

  // ── Call → Lead conversion state ──
  const [convertingCall, setConvertingCall] = useState(null);
  const [convertForm,    setConvertForm]    = useState({
    full_name: "", email: "", phone: "", propertyId: "", status: "new", notes: "", linkProperty: false,
  });
  const [convertProperties, setConvertProperties] = useState([]);
  const [converting,        setConverting]        = useState(false);

  const openConvert = (log) => {
    setConvertingCall(log);
    setConvertForm({
      full_name:    log.clientName || "",
      email:        "",
      phone:        log.contactNumber || "",
      propertyId:   log.property?._id || log.property || "",
      status:       "new",  // Default to "new" - Agent/Manager updates manually
      notes:        log.remarks || "",
      linkProperty: !!(log.property),
    });
    // Lazy-load properties when modal opens
    if (!convertProperties.length) {
      api.get("/properties")
        .then(res => setConvertProperties(Array.isArray(res.data) ? res.data : (res.data?.properties || [])))
        .catch(() => {});
    }
  };

  const handleConvertSubmit = async (e) => {
    e.preventDefault();
    if (!convertForm.full_name || !convertForm.phone) {
      alert("Full name and phone are required");
      return;
    }
    setConverting(true);
    try {
      await convertCallToLead(convertingCall._id, {
        full_name:  convertForm.full_name.trim(),
        email:      convertForm.email.trim().toLowerCase(),
        phone:      convertForm.phone.trim(),
        propertyId: convertForm.linkProperty ? convertForm.propertyId || null : null,
        status:     convertForm.status || "new",  // Lead status (Agent/Manager can update later)
        notes:      convertForm.notes,
      });
      setConvertingCall(null);
      load();
      alert("✅ Call successfully converted to lead!\n\nYou can now update the lead status in the Leads module.");
    } catch (err) {
      alert("Failed to convert: " + (err.response?.data?.message || err.message));
    } finally {
      setConverting(false);
    }
  };

  // Read URL params for pre-filtering from Dashboard
  const [statusFilter, setStatusFilter] = useState(() => {
    const p = new URLSearchParams(location.search);
    return p.get("status") || "";
  });
  const [agentFilter, setAgentFilter] = useState(() => {
    const p = new URLSearchParams(location.search);
    return p.get("agent") || "";
  });
  const [meetingFilter, setMeetingFilter] = useState(() => {
    const p = new URLSearchParams(location.search);
    return p.get("hasMeeting") || "";
  });

  // Re-apply if URL changes
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setStatusFilter(p.get("status") || "");
    setAgentFilter(p.get("agent") || "");
    setMeetingFilter(p.get("hasMeeting") || "");
  }, [location.search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to)   params.to   = to;
      const { data } = await fetchCallLogs(params);
      const sorted = [...data].sort((a, b) => new Date(b.callDate) - new Date(a.callDate));
      setLogs(sorted);
    } catch { /**/ }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this call log?")) return;
    await deleteCallLog(id);
    load();
  };

  // Build unique agent list from actual log data
  const agentOptions = [...new Map(
    logs
      .filter(l => l.createdByName)
      .map(l => [l.createdByName, { name: l.createdByName, email: l.createdByEmail }])
  ).values()].sort((a, b) => a.name.localeCompare(b.name));

  // Build unique status list
  const statusOptions = [...new Set([
    ...Object.keys(STATUS_COLOR),
    ...logs.map(l => l.currentStatus).filter(Boolean),
  ])].sort();

  // Filter logic
  const now = new Date();
  const filtered = logs.filter(l => {
    const q = search.toLowerCase();
    const matchSearch =
      l.clientName?.toLowerCase().includes(q) ||
      l.contactNumber?.includes(q) ||
      l.createdByName?.toLowerCase().includes(q) ||
      l.currentStatus?.toLowerCase().includes(q) ||
      l.lead?.full_name?.toLowerCase().includes(q) ||
      l.property?.propertyName?.toLowerCase().includes(q) ||
      l.deal?.dealName?.toLowerCase().includes(q);

    const matchStatus = statusFilter
      ? l.currentStatus?.trim().toLowerCase() === statusFilter.trim().toLowerCase()
      : true;

    const matchAgent = agentFilter
      ? l.createdByName?.trim().toLowerCase() === agentFilter.trim().toLowerCase()
      : true;

    // hasMeeting=upcoming → only logs with a future meetingScheduled date
    const matchMeeting = meetingFilter === "upcoming"
      ? l.meetingScheduled && new Date(l.meetingScheduled) >= now
      : true;

    return matchSearch && matchStatus && matchAgent && matchMeeting;
  }).sort((a, b) => new Date(b.callDate) - new Date(a.callDate));

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openAdd  = () => { setEditing(null); setModal(true); };
  const openEdit = (log) => { setEditing(log); setModal(true); };

  const hasActiveFilters = from || to || statusFilter || agentFilter || meetingFilter;

  const clearFilters = () => {
    setFrom("");
    setTo("");
    setStatusFilter("");
    setAgentFilter("");
    setMeetingFilter("");
    setPage(1);
  };

  return (
    <div className="clp-page">

      {/* Header */}
      <div className="clp-topbar">
        <div className="clp-heading">
          <div className="clp-icon-wrap"><Phone size={22}/></div>
          <div>
            <h1>Call Logs</h1>
            <p>{logs.length} total records</p>
          </div>
        </div>
        <div className="clp-header-actions">
          <button className="clp-btn-bulk" onClick={() => setBulk(true)}>
            <Upload size={15}/> Bulk Upload
          </button>
          <button className="clp-btn-add" onClick={openAdd}>
            <Plus size={16}/> Add Call Log
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="clp-filters">

        {/* Search */}
        <div className="clp-search-wrap">
          <Search size={15} className="clp-search-icon"/>
          <input
            className="clp-search"
            placeholder="Search client, lead, property, deal, status…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        <div className="clp-date-wrap">

          {/* Status filter */}
          <select
            className="clp-status-filter"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Statuses</option>
            {statusOptions.map(s => (
              <option key={s} value={s}>{formatCallStatus(s)}</option>
            ))}
          </select>

          {/* Agent filter — NEW */}
          <select
            className="clp-status-filter"
            value={agentFilter}
            onChange={(e) => { setAgentFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Agents</option>
            {agentOptions.map(a => (
              <option key={a.name} value={a.name}>{a.name}</option>
            ))}
          </select>

          {/* Date range */}
          <label>From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}/>
          <label>To</label>
          <input type="date" value={to}   onChange={(e) => setTo(e.target.value)}/>

          {hasActiveFilters && (
            <button className="clp-clear" onClick={clearFilters}>Clear</button>
          )}

        </div>
      </div>

      {/* Table */}
      <div className="clp-table-wrap">
        {loading ? (
          <div className="clp-loading"><div className="clp-spinner"/><p>Loading call logs…</p></div>
        ) : paginated.length === 0 ? (
          <div className="clp-empty">
            <Phone size={40} strokeWidth={1}/>
            <p>No call logs found</p>
            <button onClick={openAdd}>Add your first log</button>
          </div>
        ) : (
          <>
            <table className="clp-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Address</th>
                  <th>Contact</th>
                  <th>Lead / Property / Deal</th>
                  <th>Meeting</th>
                  <th>Visit</th>
                  <th>Status</th>
                  <th>Agent</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((log) => {
                  const sc = STATUS_COLOR[log.currentStatus] || { bg: "#f3f4f6", color: "#374151" };
                  return (
                    <tr key={log._id}>
                      <td>
                        <div className="clp-client">
                          <div className="clp-avatar">{log.clientName?.[0]?.toUpperCase()}</div>
                          <p className="clp-name">{log.clientName}</p>
                        </div>
                      </td>
                      <td>
                        <span className="clp-address">{log.address || <span className="clp-none">—</span>}</span>
                      </td>
                      <td><span className="clp-contact">{log.contactNumber}</span></td>
                      <td><LinkedEntities log={log}/></td>
                      <td>
                        {log.meetingScheduled
                          ? <span className="clp-meeting"><Calendar size={12}/> {new Date(log.meetingScheduled).toLocaleDateString()}</span>
                          : <span className="clp-none">—</span>}
                      </td>
                      <td>
                        <span className="clp-visit" style={{ color: VISIT_COLOR[log.visit] }}>
                          ● {log.visit}
                        </span>
                      </td>
                      <td>
                        <span className="clp-status" style={{ background: sc.bg, color: sc.color }}>
                          {formatCallStatus(log.currentStatus)}
                        </span>
                      </td>
                      <td>
                        <div className="clp-agent-cell">
                          <div className="clp-agent-avatar">
                            {(log.createdByName || "?")[0].toUpperCase()}
                          </div>
                          <span className="clp-agent">{log.createdByName || "—"}</span>
                        </div>
                      </td>
                      <td>
                        <span className="clp-date">{new Date(log.callDate).toLocaleDateString()}</span>
                      </td>
                      <td>
                        <div className="clp-actions">
                          {log.currentStatus === "Interested" && !log.convertedLeadId && (
                            <button
                              className="clp-act-btn convert"
                              title="Convert to Lead"
                              onClick={() => openConvert(log)}
                            >
                              <UserPlus size={14}/>
                            </button>
                          )}
                          {log.convertedLeadId && (
                            <span
                              className="clp-converted-tag"
                              title="This call has been converted to a lead"
                            >
                              <ArrowRight size={11}/> Converted
                            </span>
                          )}
                          <button className="clp-act-btn view" onClick={() => navigate(`/call-logs/${log._id}`)}>
                            <Eye size={14}/>
                          </button>
                          <button className="clp-act-btn edit" onClick={() => openEdit(log)}>
                            <Pencil size={14}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="clp-pagination">
                <span className="clp-page-info">
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft size={16}/>
                </button>
                <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight size={16}/>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <CallLogModal
        open={modal}
        onClose={() => setModal(false)}
        onSaved={load}
        existing={editing}
      />
      <BulkUploadModal
        open={bulk}
        onClose={() => setBulk(false)}
        onDone={() => { setBulk(false); load(); }}
      />

      {/* ── Convert Call → Lead Modal ── */}
      {convertingCall && (
        <div className="cnv-modal-overlay" onClick={() => setConvertingCall(null)}>
          <div className="cnv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cnv-modal-header">
              <div className="cnv-icon">
                <UserPlus size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <h3>Convert Call to Lead</h3>
                <p>Creating a new lead from <strong>{convertingCall.clientName}</strong>'s interested call</p>
              </div>
              <button className="cnv-close-btn" onClick={() => setConvertingCall(null)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleConvertSubmit}>
              <div className="cnv-modal-body">
                <div className="cnv-grid-2">
                  <div className="cnv-field">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={convertForm.full_name}
                      onChange={(e) => setConvertForm(p => ({ ...p, full_name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="cnv-field">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      value={convertForm.phone}
                      onChange={(e) => setConvertForm(p => ({ ...p, phone: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="cnv-field">
                  <label>Email (optional)</label>
                  <input
                    type="email"
                    placeholder="lead@example.com"
                    value={convertForm.email}
                    onChange={(e) => setConvertForm(p => ({ ...p, email: e.target.value }))}
                  />
                </div>

                <div className="cnv-field">
                  <label>Lead Status (optional)</label>
                  <select
                    value={convertForm.status}
                    onChange={(e) => setConvertForm(p => ({ ...p, status: e.target.value }))}
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="interested">Interested</option>
                    <option value="follow_up">Follow-up</option>
                    <option value="matured">Matured</option>
                    <option value="closed">Closed</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>

                <div className="cnv-checkbox-row">
                  <label className="cnv-checkbox-label">
                    <input
                      type="checkbox"
                      checked={convertForm.linkProperty}
                      onChange={(e) => setConvertForm(p => ({ ...p, linkProperty: e.target.checked }))}
                    />
                    <span>Link to a property</span>
                  </label>
                </div>

                {convertForm.linkProperty && (
                  <div className="cnv-field">
                    <label>
                      <Building2 size={13} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />
                      Select Property
                    </label>
                    <select
                      value={convertForm.propertyId}
                      onChange={(e) => setConvertForm(p => ({ ...p, propertyId: e.target.value }))}
                    >
                      <option value="">— Select a property —</option>
                      {convertProperties.map(p => (
                        <option key={p._id} value={p._id}>
                          {p.propertyName}{p.location ? ` — ${p.location}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="cnv-field">
                  <label>Notes</label>
                  <textarea
                    rows={3}
                    placeholder="Additional notes for this lead…"
                    value={convertForm.notes}
                    onChange={(e) => setConvertForm(p => ({ ...p, notes: e.target.value }))}
                  />
                </div>

                <div className="cnv-info-banner">
                  <ArrowRight size={14} />
                  <span>
                    The new lead will be created with status <strong>"{convertForm.status || 'New'}"</strong> and linked back to this call log. You can update the status later in the Leads module.
                  </span>
                </div>
              </div>

              <div className="cnv-modal-footer">
                <button type="button" className="cnv-btn-cancel" onClick={() => setConvertingCall(null)} disabled={converting}>
                  Cancel
                </button>
                <button type="submit" className="cnv-btn-submit" disabled={converting}>
                  <UserPlus size={14} />
                  {converting ? "Converting…" : "Create Lead from Call"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}